import React, { useState, useEffect } from 'react';
import { Card, Table, Form, InputGroup, Collapse, Button, Badge, Pagination } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { GetCustomersWiseStatus, getCustomerRulesetStatusBreakdown } from '../services/api/extractionApi';
import { useNavigation } from '../contexts/NavigationContext';
import { EXTRACTION_JOB_STATUS, STATUS_DISPLAY } from '../constants/statusConstants';

const CustomerReviewDashboard = () => {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState('customerName');
  const [sortDirection, setSortDirection] = useState('asc');
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [rulesetBreakdown, setRulesetBreakdown] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const navigate = useNavigate();
  const { setExtractionFilters } = useNavigation();

  useEffect(() => {
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterAndSortCustomers();
  }, [customers, searchTerm, sortField, sortDirection]);

  const fetchCustomers = async () => {
    try {
      setIsRefreshing(true);
      const response = await GetCustomersWiseStatus();
      setCustomers(response.data);
    } catch (error) {
      console.error('Error fetching customers:', error);
    } finally {
      setIsRefreshing(false);
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    await fetchCustomers();
    if (expandedCustomer) {
      await fetchRulesetBreakdown(expandedCustomer);
    }
  };

  const filterAndSortCustomers = () => {
    let filtered = [...customers];
    if (searchTerm) {
      filtered = filtered.filter(customer =>
        customer.customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        customer.customerCode.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    filtered.sort((a, b) => {
      let aValue = a[sortField] || a.statusCounts[sortField] || 0;
      let bValue = b[sortField] || b.statusCounts[sortField] || 0;
      
      if (typeof aValue === 'string') {
        return sortDirection === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
    });
    
    setFilteredCustomers(filtered);
    setCurrentPage(1);
  };

  const getPaginatedCustomers = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredCustomers.slice(startIndex, startIndex + itemsPerPage);
  };

  const totalPages = Math.ceil(filteredCustomers.length / itemsPerPage);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const fetchRulesetBreakdown = async (customerId) => {
    try {
      const response = await getCustomerRulesetStatusBreakdown(customerId);
      setRulesetBreakdown(response.data);
    } catch (error) {
      console.error('Error fetching ruleset breakdown:', error);
    }
  };

  const handleCustomerExpand = async (customer) => {
    if (expandedCustomer === customer.customerId) {
      setExpandedCustomer(null);
      setRulesetBreakdown([]);
    } else {
      setExpandedCustomer(customer.customerId);
      await fetchRulesetBreakdown(customer.customerId);
    }
  };



  const handleStatusClick = (customerId, rulesetId, status) => {
    setExtractionFilters({
      customerId,
      rulesetId,
      status
    });
    navigate('/extraction-jobs');
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return '';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  if (isLoading) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">Customer Extraction Statistics</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </Card.Body>
      </Card>
    );
  }

  if (customers.length === 0) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">Customer Extraction Statistics</h5>
        </Card.Header>
        <Card.Body className="text-center py-4">
          <p className="text-muted mb-0">No customers with extraction status at this time.</p>
        </Card.Body>
      </Card>
    );
  }

  if (filteredCustomers.length === 0) {
    return (
      <Card>
        <Card.Header>
          <h5 className="mb-0">Customer Extraction Statistics</h5>
        </Card.Header>
        <Card.Body>
          <Form className="mb-3">
            <InputGroup>
              <Form.Control
                type="text"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Form>
          <div className="text-center py-4">
            <p className="text-muted mb-0">No customers match your search criteria.</p>
          </div>
        </Card.Body>
      </Card>
    );
  }

  return (
    <Card>
      <Card.Header>
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0">Customer Extraction Statistics</h5>
          <div className="d-flex align-items-center gap-2">
            <button 
              className="btn btn-outline-primary btn-sm px-2 py-1"
              onClick={handleRefresh}
              disabled={isRefreshing}
              style={{ fontSize: '14px', lineHeight: '1' }}
            >
              {isRefreshing ? '⟳' : '↻'}
            </button>
            <Badge bg="warning" className="px-2 py-1" style={{ fontSize: '12px' }}>{filteredCustomers.length} customers</Badge>
          </div>
        </div>
      </Card.Header>
      <Card.Body>
        <Form className="mb-3">
          <InputGroup>
            <Form.Control
              type="text"
              placeholder="Search customers..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Form>
        <Table responsive hover>
          <thead className="table-light">
            <tr>
              <th 
                style={{ cursor: 'pointer' }} 
                onClick={() => handleSort('customerName')}
              >
                Customer {getSortIcon('customerName')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('requested')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.REQUESTED]} {getSortIcon('requested')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('processing')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.PROCESSING]} {getSortIcon('processing')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('augmenting')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.AUGMENTING]} {getSortIcon('augmenting')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('augmentationFailed')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.AUGMENTATION_FAILED]} {getSortIcon('augmentationFailed')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('augmentationReview')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW]} {getSortIcon('augmentationReview')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('needsReview')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.NEEDS_REVIEW]} {getSortIcon('needsReview')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('readyForExport')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.READY_FOR_EXPORT]} {getSortIcon('readyForExport')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('completed')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.COMPLETED]} {getSortIcon('completed')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('failed')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.FAILED]} {getSortIcon('failed')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('exporting')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.EXPORTING]} {getSortIcon('exporting')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('exportedLast7Days')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.EXPORTED]} (7d) {getSortIcon('exportedLast7Days')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('exportFailed')}
              >
                {STATUS_DISPLAY[EXTRACTION_JOB_STATUS.EXPORT_FAILED]} {getSortIcon('exportFailed')}
              </th>
              <th 
                style={{ cursor: 'pointer', textAlign: 'right' }} 
                onClick={() => handleSort('total')}
              >
                Total {getSortIcon('total')}
              </th>
            </tr>
          </thead>
          <tbody>
            {getPaginatedCustomers().map((customer) => (
              <React.Fragment key={customer.customerId}>
                <tr>
                  <td>
                    <button 
                      className="btn btn-link p-0 text-start text-decoration-none"
                      onClick={() => handleCustomerExpand(customer)}
                      style={{ color: '#212529' }}
                    >
                      <strong>{customer.customerName}</strong>
                      <span className="text-muted ms-2">({customer.customerCode})</span>
                    </button>
                  </td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.requested}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.processing}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.augmenting || 0}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.augmentationFailed || 0}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.augmentationReview || 0}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.needsReview}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.readyForExport || 0}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.completed}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.failed}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.exporting || 0}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.exportedLast7Days || 0}</td>
                  <td style={{ textAlign: 'right' }}>{customer.statusCounts.exportFailed || 0}</td>
                  <td style={{ textAlign: 'right' }}><strong>{customer.statusCounts.total}</strong></td>
                </tr>
                <tr>
                  <td colSpan="14" className="p-0">
                    <Collapse in={expandedCustomer === customer.customerId}>
                      <div className="p-3" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0d6efd' }}>
                        <h6 className="text-primary mb-3">Ruleset Breakdown</h6>
                        <Table size="sm" className="bg-white">
                          <thead className="table-secondary">
                            <tr>
                              <th>Ruleset</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.REQUESTED]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.PROCESSING]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.AUGMENTING]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.AUGMENTATION_FAILED]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.NEEDS_REVIEW]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.READY_FOR_EXPORT]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.COMPLETED]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.FAILED]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.EXPORTING]}</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.EXPORTED]} (7d)</th>
                              <th style={{ textAlign: 'right' }}>{STATUS_DISPLAY[EXTRACTION_JOB_STATUS.EXPORT_FAILED]}</th>
                              <th style={{ textAlign: 'right' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {rulesetBreakdown.map((ruleset) => (
                              <tr key={ruleset.rulesetId}>
                                <td><strong>{ruleset.rulesetName}</strong></td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.requested > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.REQUESTED)}
                                    >
                                      {ruleset.statusCounts.requested}
                                    </button>
                                  ) : ruleset.statusCounts.requested}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.processing > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.PROCESSING)}
                                    >
                                      {ruleset.statusCounts.processing}
                                    </button>
                                  ) : ruleset.statusCounts.processing}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.augmenting > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.AUGMENTING)}
                                    >
                                      {ruleset.statusCounts.augmenting}
                                    </button>
                                  ) : (ruleset.statusCounts.augmenting || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.augmentationFailed > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.AUGMENTATION_FAILED)}
                                    >
                                      {ruleset.statusCounts.augmentationFailed}
                                    </button>
                                  ) : (ruleset.statusCounts.augmentationFailed || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.augmentationReview > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.AUGMENTATION_REVIEW)}
                                    >
                                      {ruleset.statusCounts.augmentationReview}
                                    </button>
                                  ) : (ruleset.statusCounts.augmentationReview || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.needsReview > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.NEEDS_REVIEW)}
                                    >
                                      {ruleset.statusCounts.needsReview}
                                    </button>
                                  ) : ruleset.statusCounts.needsReview}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.readyForExport > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.READY_FOR_EXPORT)}
                                    >
                                      {ruleset.statusCounts.readyForExport}
                                    </button>
                                  ) : (ruleset.statusCounts.readyForExport || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.completed > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.COMPLETED)}
                                    >
                                      {ruleset.statusCounts.completed}
                                    </button>
                                  ) : ruleset.statusCounts.completed}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.failed > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.FAILED)}
                                    >
                                      {ruleset.statusCounts.failed}
                                    </button>
                                  ) : ruleset.statusCounts.failed}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.exporting > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.EXPORTING)}
                                    >
                                      {ruleset.statusCounts.exporting}
                                    </button>
                                  ) : (ruleset.statusCounts.exporting || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.exportedLast7Days > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.EXPORTED)}
                                    >
                                      {ruleset.statusCounts.exportedLast7Days}
                                    </button>
                                  ) : (ruleset.statusCounts.exportedLast7Days || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}>
                                  {ruleset.statusCounts.exportFailed > 0 ? (
                                    <button 
                                      className="btn btn-link p-0 text-decoration-none"
                                      onClick={() => handleStatusClick(customer.customerId, ruleset.rulesetId, EXTRACTION_JOB_STATUS.EXPORT_FAILED)}
                                    >
                                      {ruleset.statusCounts.exportFailed}
                                    </button>
                                  ) : (ruleset.statusCounts.exportFailed || 0)}
                                </td>
                                <td style={{ textAlign: 'right' }}><strong>{ruleset.statusCounts.total}</strong></td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </div>
                    </Collapse>
                  </td>
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </Table>
        {totalPages > 1 && (
          <div className="d-flex justify-content-center mt-3">
            <Pagination size="sm">
              <Pagination.Prev 
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(currentPage - 1)}
              />
              {[...Array(totalPages)].map((_, index) => (
                <Pagination.Item
                  key={index + 1}
                  active={index + 1 === currentPage}
                  onClick={() => setCurrentPage(index + 1)}
                >
                  {index + 1}
                </Pagination.Item>
              ))}
              <Pagination.Next 
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage(currentPage + 1)}
              />
            </Pagination>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default CustomerReviewDashboard;